import {
  ArrowForward as ArrowForwardIcon,
  ArrowBack as BackIcon,
  CheckCircle as CheckIcon,
  Email as EmailIcon,
  ExpandMore as ExpandMoreIcon,
  Google as GoogleIcon,
  InfoOutlined as InfoOutlinedIcon,
  Psychology as KnowledgeIcon,
  Lightbulb as LightbulbIcon,
  WarningAmber as WarningIcon,
} from '@mui/icons-material';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Link,
  Typography,
} from '@mui/material';
import type React from 'react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AppDialog from '../components/AppDialog';
import { useThemeMode } from '../contexts/ThemeContext';
import { useThemeColors } from '../hooks/useThemeColors';
import { IMG } from '../theme/assets';

/** ヘルプ機能ガイドの各トピック（mock TOPICS 準拠）。 */
type Topic = {
  title: string;
  color: string;
  /** カードのイラスト背景 tint（中心色） */
  tint: string;
  ill: string;
  d: string;
  overview: string;
  steps: string[];
  tips: string[];
};

const TOPICS: Topic[] = [
  {
    title: 'ドキュメントの検証',
    color: '#5b56e0',
    tint: '#eef0fb',
    ill: IMG.help.document,
    d: '資料から主張を自動抽出し、根拠つきで検証します。',
    overview:
      'PDF・Excel・Word・PowerPoint をアップロードすると、含まれる主張をAIが自動で抽出し、信頼できる情報源と照らし合わせて、根拠つきで一つひとつ検証します。',
    steps: [
      'マイページの「文書のファクトチェック」を選びます。',
      '資料の種別・名称・基準時点・概要を入力し、資料をドラッグ＆ドロップでアップロードします。',
      '抽出された主張を確認し、不要なものは編集・削除します。',
      '各主張の調べ方（AIの知識／ネット検索）を選びます。',
      '「検証を開始」で実行し、4分類の判定と根拠を確認します。',
    ],
    tips: [
      '「基準時点」は資料が作成・収録された日です。本文中の「今」「最近」がいつを指すかの判断に使われるため、正確に入力してください。',
      'Excel は表の行・列の対応を保ったまま読み取ります。PDFに変換せず、そのままアップロードしてください。',
      'テキストを含むPDFを使ってください（スキャン画像はOCRで読み取るため、処理に時間がかかります）。',
      'スキャンPDFの処理はバックグラウンドで継続するので、画面を離れても大丈夫です。',
      '誤って抽出された主張は、その場で編集・削除できます。',
      '最新の出来事は「ネット検索」を選ぶと精度が上がります。',
      '引用の箇所は結果画面の「引用の確認方法」からPDF内検索で確認できます。',
    ],
  },
  {
    title: '動画の検証',
    color: '#0d9488',
    tint: '#e7f6f1',
    ill: IMG.help.video,
    d: '動画を文字起こしし、発言をタイムコード付きで検証。',
    overview:
      '動画をAIで文字起こしし、発言の事実性をタイムコード付きで検証します。処理はバックグラウンドで進みます。',
    steps: [
      '「動画のファクトチェック」を選び、資料の種別・名称・基準時点・概要を入力して言語を選びます。',
      'MP4 / MOV / WebM の動画をアップロードします（最大20GB）。',
      '文字起こし → 主張の抽出 → 検証が自動で進みます。',
      '完了後、結果をタイムコード付きで確認します。',
    ],
    tips: [
      '対応形式・サイズ上限（20GB）にご注意ください。',
      '音声が明瞭なほど文字起こしの精度が上がります。',
      '処理はバックグラウンドで継続するので、画面を離れても大丈夫です。完了後は一覧から続きを再開できます。',
      '処理中の画面で待っていた場合は、完了すると主張の確認画面へ自動で進みます。',
      '結果のタイムコードから、該当の発言箇所へ頭出しできます。',
    ],
  },
  {
    title: 'AIによる追加リサーチ',
    color: '#7c5cff',
    tint: '#f1eefb',
    ill: IMG.help.followup,
    d: '検証済みの主張をAIがさらに深掘りします。',
    overview:
      '検証済みの主張を、AIがさらに深掘り。背景や根拠を追加で調査できます。専用の調査チャットで対話的に掘り下げられます。',
    steps: [
      '結果画面で、気になる主張の「AIによる追加リサーチ」を押します。',
      '調査チャットで質問を入力します。',
      '「戦略立案」「横断検索」エージェントを内容に合わせて使い分けます。',
      '横断検索では対象期間（1日〜1年）を指定できます。',
    ],
    tips: [
      '質問は具体的にするほど精度が上がります。',
      '横断検索は対象期間を絞ると関連性が高まります。',
      '回答に付く出典は必ず確認しましょう。',
    ],
  },
  {
    title: 'ドメイン制限ファクトチェック',
    color: '#e08600',
    tint: '#fdf2e0',
    ill: IMG.help.domain,
    d: '信頼ドメインだけに絞り出典を照合して検証。',
    overview:
      '信頼できるドメインだけに絞って、出典を照合しながら検証します。情報源を限定することで根拠の確かさを高められます。',
    steps: [
      '「特定のサイトに限定してファクトチェック」を選びます。',
      '調べたい内容を入力し、主張と検索クエリを生成します。',
      '照合する信頼ドメインを選びます（必要なら「ドメイン管理」で編集）。',
      '「ファクトチェック実行」で、判定と出典を確認します。',
    ],
    tips: [
      '対象ドメインを絞るほど、根拠の信頼性が高まります。',
      '公式テンプレートは編集不可です。自分用は「マイリスト」で追加できます。',
      '選択できるドメイン数の上限（300）にご注意ください。',
    ],
  },
  {
    title: '放送規程チェック',
    color: '#15803d',
    tint: '#e7f3ea',
    ill: IMG.compliance.owlShield,
    d: '放送ハンドブックと放送基準に照らして点検します。',
    overview:
      '動画とドキュメントを、放送ハンドブックと民放連 放送基準（計737条）に照らして点検します。抵触しそうな表現を、根拠とした条文つきで指摘します。',
    steps: [
      '左メニューの「放送規程チェック」を開きます。',
      '抽出・解析が済んだ資料が一覧に出るので、点検したいものを選びます。',
      '「チェックする」で実行します（バックグラウンドで進みます）。',
      '一覧・詳細・素材の3列で、指摘と根拠の条文を確認します。',
      '指摘を選ぶと、動画は該当の時刻へ、資料は該当のページへ移動します。',
    ],
    tips: [
      '先にファクトチェック（抽出・解析）を済ませてください。未処理の資料は一覧に出ません。',
      '判定は ×（抵触の可能性）と △（要確認）の2つです。該当する条文が見つからなかったものは指摘になりません。',
      '指摘は「発話」と「情景」（映像から読み取った状況）を分けて出します。',
      'CMの区間は斜線で示され、点検の対象外です。',
      '不採用になった懸念も一覧の下に畳んで出します。見落としか問題なしかを区別するためです。',
      '条文は章・見出し・逐語引用つきです。放送基準はページ番号、ハンドブックは該当ページへ移動できます。',
      '規程のPDFを開くにはログインが必要です。',
    ],
  },
  {
    title: '案件（グループ共有）',
    color: '#0891b2',
    tint: '#e6f6fb',
    ill: IMG.workspace.owl,
    d: 'グループで検証を共有・共同作業できます。',
    overview:
      'グループでファクトチェックを共有・共同作業できる機能です。グループで同じ資料の検証状況を確認できます。',
    steps: [
      '設定でグループを作成します（メンバーを招待）。',
      '「案件」から新規作成し、共有するグループを選びます。',
      '案件にアイテム（ドキュメント／動画）を追加します。',
      'メンバー全員で検証状況や結果を共有・確認します。',
      '招待リンクから新しいメンバーを追加できます。',
    ],
    tips: [
      'オーナーとメンバーで操作できる範囲が異なります。',
      '招待リンクの発行は設定の「グループ管理」から行えます。',
      'アイテムの検証進捗（抽出／検証済み）はメンバー全員が確認できます。',
    ],
  },
];

const FAQ: { q: string; a: string }[] = [
  {
    q: 'どのようなPDFファイルに対応していますか？',
    a: 'テキストを含むPDFに対応しています。スキャン画像のみのPDFは、文字を読み取れず抽出精度が下がる場合があります。',
  },
  {
    q: '動画はどの形式・サイズに対応していますか？',
    a: 'MP4 / MOV / WebM に対応し、最大20GBまでアップロードできます。',
  },
  {
    q: '検証結果はどのように判定されますか？',
    a: '正確／不正確／部分的に正確／検証不可の4分類で、根拠と出典を添えて判定します。',
  },
  {
    q: '「AIの知識」と「ネット検索」はどう使い分けますか？',
    a: '一般的な事実や素早い確認は「AIの知識」、最新情報や出典の明示が必要な場合は「ネット検索」が向いています。',
  },
  {
    q: '抽出された主張がずれている場合は？',
    a: '抽出レビュー画面で、各主張をその場で編集・削除できます。検証する主張だけを選ぶことも可能です。',
  },
  {
    q: '検証にはどのくらい時間がかかりますか？',
    a: '主張の件数や調べ方、動画の長さによって変わります。動画とスキャンPDFはバックグラウンドで処理が継続するため、画面を離れても大丈夫です。完了後は一覧から続きを再開できます。',
  },
  {
    q: '判定が「検証不可」になるのはなぜですか？',
    a: '将来の予測や、真偽を裏づける一次情報が乏しい主張など、現時点で事実を確定できない場合に「検証不可」となります。',
  },
  {
    q: 'AIによる追加リサーチでは何ができますか？',
    a: '検証済みの主張をAIがさらに深掘りし、背景や根拠を追加で調べられます。戦略立案・横断検索エージェントを使い分けられます。',
  },
  {
    q: 'ドメイン制限は何のためにありますか？',
    a: '信頼できる情報源だけに絞ることで、根拠の確かさを高めるための機能です。公式テンプレートに加え、自分用の「マイリスト」も作れます。',
  },
  {
    q: '放送規程チェックは何に照らして判定していますか？',
    a: '放送ハンドブックと民放連 放送基準の2冊（計737条）に照らします。指摘には根拠とした条文を、章・見出し・逐語引用つきで添えています。',
  },
  {
    q: '規程チェックの × と △ の違いは？',
    a: '×は抵触の可能性がある指摘、△は要確認の指摘です。該当する条文が見つからなかったものは指摘になりません。',
  },
  {
    q: '規程チェックで指摘が0件でした。問題なしということですか？',
    a: '該当する条文が見つからなかっただけの場合もあります。走査で拾ったものの不採用になった懸念を一覧の下に畳んで出しているので、そちらもご確認ください。',
  },
  {
    q: '規程チェックはどの資料に実行できますか？',
    a: '抽出・解析が済んだ資料に実行できます。アップロードしただけの資料は一覧に出ないので、先にファクトチェックを済ませてください。',
  },
  {
    q: 'グループで共有して使うには？',
    a: '案件とグループ機能を使います。グループを作成し、招待リンクからメンバーを追加すると、検証状況を共有できます。',
  },
  {
    q: 'データは安全に保存されますか？',
    a: 'アップロードした資料と結果はアカウントごとに安全に保管され、許可した相手以外には共有されません。',
  },
  {
    q: '検証に失敗する場合の対処法は？',
    a: 'ネットワーク状況や対象資料の形式を確認のうえ、再実行してください。改善しない場合はサポートまでご連絡ください。',
  },
];

const Help: React.FC = () => {
  const navigate = useNavigate();
  const { themeMode } = useThemeMode();
  const tokens = useThemeColors();
  const dark = themeMode === 'dark';

  const [openTopic, setOpenTopic] = useState<Topic | null>(null);

  const sectionHeading = (title: string, sub: string) => (
    <Box sx={{ mt: 3.5, mb: 2 }}>
      <Typography sx={{ fontSize: 18, fontWeight: 900, color: tokens.ink, lineHeight: 1.2 }}>
        {title}
      </Typography>
      <Typography sx={{ fontSize: 12.5, color: tokens.muted, mt: 0.25 }}>{sub}</Typography>
    </Box>
  );

  return (
    <Box sx={{ maxWidth: 1380, mx: 'auto', width: '100%', pb: 4 }}>
      {/* ヘッダー */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.75, mb: 1 }}>
        <Button
          startIcon={<BackIcon sx={{ fontSize: 18 }} />}
          onClick={() => navigate('/')}
          sx={{
            textTransform: 'none',
            fontSize: 13,
            fontWeight: 700,
            color: tokens.ink,
            bgcolor: dark ? 'rgba(255,255,255,0.06)' : 'rgba(255,255,255,0.9)',
            border: `1px solid ${tokens.line}`,
            borderRadius: '11px',
            px: 1.75,
            py: 1,
            '&:hover': {
              bgcolor: dark ? 'rgba(255,255,255,0.1)' : '#fff',
            },
          }}
        >
          マイページに戻る
        </Button>
        <Typography sx={{ fontSize: 21, fontWeight: 900, color: tokens.ink }}>ヘルプ</Typography>
      </Box>

      {/* ヒーロー（案A型・左にフクロウ） */}
      <Box
        sx={{
          position: 'relative',
          overflow: 'hidden',
          display: 'flex',
          alignItems: 'center',
          gap: { xs: 2.5, sm: 3.25 },
          background: dark
            ? `linear-gradient(120deg, ${tokens.heroFrom}, ${tokens.heroTo})`
            : 'linear-gradient(120deg,#eef0fb,#e7f6f1)',
          border: `1px solid ${tokens.line}`,
          borderRadius: '17px',
          p: { xs: '22px 24px', sm: '26px 40px' },
          mt: 1.25,
          mb: 1,
          boxShadow: '0 10px 30px rgba(30,30,70,.07)',
        }}
      >
        <Box
          component="img"
          src={IMG.owl.hero}
          alt=""
          sx={{
            width: { xs: 92, sm: 118 },
            height: { xs: 92, sm: 118 },
            objectFit: 'contain',
            flexShrink: 0,
            filter: 'drop-shadow(0 10px 18px rgba(30,30,70,.18))',
          }}
        />
        <Box>
          <Typography
            sx={{ fontSize: { xs: 22, sm: 27 }, fontWeight: 900, color: tokens.ink, m: 0 }}
          >
            MIKIWAME ヘルプ
          </Typography>
          <Typography
            sx={{
              fontSize: 13.5,
              color: tokens.muted,
              mt: 1,
              lineHeight: 1.6,
              maxWidth: 780,
            }}
          >
            主張を「根拠」から見極めるための機能を紹介します。各カードを押すと、使い方の手順と気をつけるポイントが見られます。
          </Typography>
        </Box>
      </Box>

      {/* 機能ガイド */}
      {sectionHeading('機能ガイド', 'カードを押すと詳しい使い方が開きます')}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            sm: '1fr 1fr',
            md: 'repeat(3, 1fr)',
            lg: 'repeat(6, 1fr)',
          },
          gap: 2,
        }}
      >
        {TOPICS.map((t) => (
          <Box
            key={t.title}
            onClick={() => setOpenTopic(t)}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              bgcolor: tokens.paper,
              border: `1px solid ${tokens.line}`,
              borderRadius: '14px',
              overflow: 'hidden',
              boxShadow: '0 8px 24px rgba(30,30,70,.06)',
              cursor: 'pointer',
              transition: '.18s',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: '0 18px 38px rgba(30,30,70,.13)',
              },
            }}
          >
            <Box
              sx={{
                height: 140,
                display: 'grid',
                placeItems: 'center',
                background: dark
                  ? 'transparent'
                  : `radial-gradient(circle at 50% 42%, ${t.tint}, #fff)`,
              }}
            >
              <Box
                component="img"
                src={t.ill}
                alt=""
                sx={{
                  width: 118,
                  height: 118,
                  objectFit: 'contain',
                  filter: 'drop-shadow(0 10px 18px rgba(30,30,70,.18))',
                }}
              />
            </Box>
            <Box sx={{ p: '15px 17px 16px', display: 'flex', flexDirection: 'column', flex: 1 }}>
              <Box
                component="span"
                sx={{
                  fontSize: 10,
                  fontWeight: 900,
                  color: '#fff',
                  borderRadius: 999,
                  px: 1.25,
                  py: '2px',
                  alignSelf: 'flex-start',
                  mb: 1,
                  bgcolor: t.color,
                }}
              >
                機能
              </Box>
              <Typography
                sx={{
                  fontSize: 14.5,
                  fontWeight: 900,
                  color: tokens.ink,
                  lineHeight: 1.35,
                  mb: 0.9,
                }}
              >
                {t.title}
              </Typography>
              <Typography sx={{ fontSize: 11.5, color: tokens.muted, lineHeight: 1.6, flex: 1 }}>
                {t.d}
              </Typography>
              <Box
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 0.6,
                  fontSize: 11.5,
                  fontWeight: 800,
                  mt: 1.5,
                  color: t.color,
                }}
              >
                詳しく見る
                <ArrowForwardIcon sx={{ fontSize: 13 }} />
              </Box>
            </Box>
          </Box>
        ))}
      </Box>

      {/* 調べ方のちがい */}
      {sectionHeading(
        '調べ方のちがい',
        '主張の内容に合わせて「AIの知識」か「ネット検索」を選べます',
      )}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2.25 }}>
        {/* AIの知識で検証 */}
        <Box
          sx={{
            border: `1.5px solid ${dark ? tokens.line : 'rgba(13,148,136,.3)'}`,
            borderRadius: '13px',
            p: '20px 22px',
            background: dark ? tokens.paper : 'linear-gradient(180deg,#f1fbf8,#fff)',
            boxShadow: '0 6px 18px rgba(30,30,70,.05)',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, fontWeight: 900, mb: 1.6 }}>
            <Box
              sx={{
                width: 34,
                height: 34,
                borderRadius: '10px',
                display: 'grid',
                placeItems: 'center',
                flexShrink: 0,
                bgcolor: dark ? 'rgba(45,212,191,0.15)' : '#eafaf6',
                color: tokens.teal,
              }}
            >
              <KnowledgeIcon sx={{ fontSize: 19 }} />
            </Box>
            <Typography sx={{ fontSize: 15.5, fontWeight: 900, color: tokens.ink }}>
              AIの知識で検証
            </Typography>
          </Box>
          <Typography sx={{ fontSize: 10.5, fontWeight: 900, color: tokens.faint, mb: 0.75 }}>
            向いている
          </Typography>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 1,
              fontSize: 12.5,
              lineHeight: 1.55,
              color: tokens.ink,
              mb: 0.75,
            }}
          >
            <CheckIcon sx={{ fontSize: 16, color: '#16a34a', mt: '2px', flexShrink: 0 }} />
            高速処理／一般的な事実に強い
          </Box>
          <Typography
            sx={{ fontSize: 10.5, fontWeight: 900, color: tokens.faint, mt: 1.6, mb: 0.75 }}
          >
            留意点
          </Typography>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 1,
              fontSize: 12.5,
              lineHeight: 1.55,
              color: tokens.ink,
            }}
          >
            <WarningIcon sx={{ fontSize: 16, color: '#e08600', mt: '2px', flexShrink: 0 }} />
            最新情報・専門分野に弱い
          </Box>
        </Box>

        {/* ネット検索で検証 */}
        <Box
          sx={{
            border: `1.5px solid ${dark ? tokens.line : 'rgba(91,86,224,.25)'}`,
            borderRadius: '13px',
            p: '20px 22px',
            background: dark ? tokens.paper : 'linear-gradient(180deg,#f3f2ff,#fff)',
            boxShadow: '0 6px 18px rgba(30,30,70,.05)',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, fontWeight: 900, mb: 1.6 }}>
            <Box
              sx={{
                width: 34,
                height: 34,
                borderRadius: '10px',
                display: 'grid',
                placeItems: 'center',
                flexShrink: 0,
                bgcolor: dark ? 'rgba(255,255,255,0.06)' : '#fff',
                border: `1px solid ${tokens.line}`,
                color: tokens.indigo,
              }}
            >
              <GoogleIcon sx={{ fontSize: 19 }} />
            </Box>
            <Typography sx={{ fontSize: 15.5, fontWeight: 900, color: tokens.ink }}>
              ネット検索で検証
            </Typography>
          </Box>
          <Typography sx={{ fontSize: 10.5, fontWeight: 900, color: tokens.faint, mb: 0.75 }}>
            向いている
          </Typography>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 1,
              fontSize: 12.5,
              lineHeight: 1.55,
              color: tokens.ink,
              mb: 0.75,
            }}
          >
            <CheckIcon sx={{ fontSize: 16, color: '#16a34a', mt: '2px', flexShrink: 0 }} />
            最新情報に対応／出典を明示
          </Box>
          <Typography
            sx={{ fontSize: 10.5, fontWeight: 900, color: tokens.faint, mt: 1.6, mb: 0.75 }}
          >
            留意点
          </Typography>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 1,
              fontSize: 12.5,
              lineHeight: 1.55,
              color: tokens.ink,
            }}
          >
            <WarningIcon sx={{ fontSize: 16, color: '#e08600', mt: '2px', flexShrink: 0 }} />
            処理時間が長め
          </Box>
        </Box>
      </Box>

      {/* よくある質問 */}
      {sectionHeading('よくある質問', '気になる項目を押すと回答が開きます')}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
          gap: 1.5,
          alignItems: 'start',
        }}
      >
        {FAQ.map((f, i) => (
          <Accordion
            key={f.q}
            defaultExpanded={i < 2}
            disableGutters
            elevation={0}
            sx={{
              bgcolor: tokens.paper,
              border: `1px solid ${tokens.line}`,
              borderRadius: '13px !important',
              overflow: 'hidden',
              '&:before': { display: 'none' },
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon sx={{ color: tokens.faint, fontSize: 20 }} />}
              sx={{
                px: 2,
                py: 0.5,
                minHeight: 'unset',
                '& .MuiAccordionSummary-content': { my: 1.25, gap: 1.25, alignItems: 'center' },
              }}
            >
              <Box
                component="span"
                sx={{
                  width: 22,
                  height: 22,
                  borderRadius: '6px',
                  display: 'grid',
                  placeItems: 'center',
                  flexShrink: 0,
                  fontWeight: 900,
                  fontSize: 12,
                  bgcolor: dark ? 'rgba(129,140,248,0.18)' : '#eef1fb',
                  color: tokens.indigo,
                }}
              >
                Q
              </Box>
              <Typography sx={{ fontSize: 13, fontWeight: 800, color: tokens.ink }}>
                {f.q}
              </Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ pl: 6, pr: 2, pb: 1.75, pt: 0 }}>
              <Typography sx={{ fontSize: 12.5, lineHeight: 1.7, color: tokens.muted }}>
                {f.a}
              </Typography>
            </AccordionDetails>
          </Accordion>
        ))}
      </Box>

      {/* 困ったときは */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          background: 'linear-gradient(120deg,#0d9488,#5b56e0)',
          color: '#fff',
          borderRadius: '14px',
          p: '22px 28px',
          mt: 2.25,
          boxShadow: '0 12px 28px rgba(40,50,90,.22)',
        }}
      >
        <Box
          sx={{
            width: 46,
            height: 46,
            borderRadius: '13px',
            bgcolor: 'rgba(255,255,255,.18)',
            display: 'grid',
            placeItems: 'center',
            flexShrink: 0,
          }}
        >
          <EmailIcon sx={{ fontSize: 24 }} />
        </Box>
        <Box>
          <Typography sx={{ fontSize: 15, fontWeight: 900, m: 0, color: '#fff' }}>
            困ったときは
          </Typography>
          <Typography
            sx={{ fontSize: 12.5, mt: 0.6, lineHeight: 1.55, color: 'rgba(255,255,255,.94)' }}
          >
            ご質問やトラブルが発生した場合は、{' '}
            <Link
              href="mailto:ints-ex-aisb-dev@ex5ch.com"
              sx={{ color: '#fff', fontWeight: 900, textDecoration: 'underline' }}
            >
              ints-ex-aisb-dev@ex5ch.com
            </Link>{' '}
            までご連絡ください。
          </Typography>
        </Box>
      </Box>

      {/* 機能詳細モーダル */}
      <AppDialog
        open={Boolean(openTopic)}
        onClose={() => setOpenTopic(null)}
        icon={openTopic ? <Box component="img" src={openTopic.ill} alt="" /> : undefined}
        title={openTopic?.title ?? ''}
        subtitle={openTopic?.d}
        size="md"
      >
        {openTopic && (
          <>
            <Box sx={{ p: '18px 24px', overflowY: 'auto' }}>
              <Typography sx={{ fontSize: 13.5, lineHeight: 1.7, color: tokens.muted, mb: 2.25 }}>
                {openTopic.overview}
              </Typography>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.4 }}>
                <LightbulbIcon sx={{ fontSize: 16, color: tokens.teal }} />
                <Typography sx={{ fontSize: 12.5, fontWeight: 900, color: tokens.ink }}>
                  使い方の手順
                </Typography>
              </Box>
              <Box
                component="ol"
                sx={{
                  listStyle: 'none',
                  m: 0,
                  mb: 2.5,
                  p: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 1.25,
                }}
              >
                {openTopic.steps.map((s, i) => (
                  <Box
                    component="li"
                    key={s}
                    sx={{
                      display: 'flex',
                      gap: 1.5,
                      fontSize: 13,
                      lineHeight: 1.55,
                      color: tokens.ink,
                    }}
                  >
                    <Box
                      sx={{
                        width: 24,
                        height: 24,
                        borderRadius: '50%',
                        bgcolor: tokens.teal,
                        color: '#fff',
                        fontWeight: 900,
                        fontSize: 12,
                        display: 'grid',
                        placeItems: 'center',
                        flexShrink: 0,
                      }}
                    >
                      {i + 1}
                    </Box>
                    <Box sx={{ pt: '2px' }}>{s}</Box>
                  </Box>
                ))}
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.4 }}>
                <InfoOutlinedIcon sx={{ fontSize: 16, color: tokens.teal }} />
                <Typography sx={{ fontSize: 12.5, fontWeight: 900, color: tokens.ink }}>
                  気をつけるポイント
                </Typography>
              </Box>
              <Box
                component="ul"
                sx={{
                  listStyle: 'none',
                  m: 0,
                  p: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 1.1,
                }}
              >
                {openTopic.tips.map((t) => (
                  <Box
                    component="li"
                    key={t}
                    sx={{
                      display: 'flex',
                      gap: 1.1,
                      fontSize: 12.5,
                      lineHeight: 1.6,
                      color: tokens.ink,
                      bgcolor: dark ? 'rgba(224,134,0,0.1)' : '#fff7ec',
                      border: `1px solid ${dark ? 'rgba(224,134,0,0.3)' : '#f6e2c0'}`,
                      borderRadius: '11px',
                      p: '10px 13px',
                    }}
                  >
                    <InfoOutlinedIcon
                      sx={{ fontSize: 15, color: '#e08600', mt: '1px', flexShrink: 0 }}
                    />
                    {t}
                  </Box>
                ))}
              </Box>
            </Box>
          </>
        )}
      </AppDialog>
    </Box>
  );
};

export default Help;
